import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Download, Trash2, RefreshCw, LayoutGrid, Table2, MessagesSquare,
  FileText, KeyRound, Phone, Mail, MapPin, Star, X, Save,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "neu", label: "Neu" },
  { key: "kontaktiert", label: "Kontaktiert" },
  { key: "analyse_geplant", label: "Analyse geplant" },
  { key: "angebot_erstellt", label: "Angebot erstellt" },
  { key: "gewonnen", label: "Gewonnen" },
  { key: "verloren", label: "Verloren" },
];
const statusLabel = (k) => STATUSES.find((s) => s.key === k)?.label || k;

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const [tab, setTab] = useState("pipeline");
  const [leads, setLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [loading, setLoading] = useState(false);

  const hdr = useCallback(() => ({ headers: { Authorization: `Bearer ${token()}` } }), [token]);

  const loadLeads = useCallback(async () => {
    const { data } = await axios.get(`${API}/leads`, hdr());
    setLeads(data);
  }, [hdr]);
  const loadSessions = useCallback(async () => {
    const { data } = await axios.get(`${API}/chat/sessions`, hdr());
    setSessions(data);
  }, [hdr]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { await Promise.all([loadLeads(), loadSessions()]); } finally { setLoading(false); }
  }, [loadLeads, loadSessions]);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  if (user === null) return <div className="min-h-screen bg-[#0E0F12]" />;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const setStatus = async (lead, status) => {
    let order_value = lead.order_value;
    if (status === "gewonnen") {
      const v = window.prompt("Auftragswert in EUR (für Offline-Conversion):", lead.order_value || "");
      if (v !== null) order_value = parseFloat(v) || 0;
    }
    await axios.patch(`${API}/leads/${lead.id}`, { status, order_value }, hdr());
    loadLeads();
  };

  const delLead = async (id) => {
    if (!window.confirm("Diesen Lead löschen?")) return;
    await axios.delete(`${API}/leads/${id}`, hdr());
    loadLeads();
  };
  const delSelected = async () => {
    if (!selected.length || !window.confirm(`${selected.length} Leads löschen?`)) return;
    await axios.post(`${API}/leads/delete`, { ids: selected }, hdr());
    setSelected([]); loadLeads();
  };
  const delAllLeads = async () => {
    if (!window.confirm("ALLE Leads unwiderruflich löschen?")) return;
    await axios.post(`${API}/leads/delete`, { all: true }, hdr());
    loadLeads();
  };
  const delSession = async (sid) => {
    if (!window.confirm("Diesen Chat löschen?")) return;
    await axios.delete(`${API}/chat/sessions/${sid}`, hdr());
    setActiveChat(null); loadSessions();
  };
  const delAllSessions = async () => {
    if (!window.confirm("ALLE Chats löschen?")) return;
    await axios.post(`${API}/chat/sessions/delete`, { all: true }, hdr());
    setActiveChat(null); loadSessions();
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/leads/export`, { ...hdr(), responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSel = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      data-testid={`tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        tab === id ? "bg-[#3DDC97] text-[#0E0F12]" : "text-gray-400 hover:bg-white/5"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0E0F12] text-gray-200">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-[#0E0F12]/90 px-6 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-6">
          <span className="font-display font-extrabold text-white">BA<span className="text-[#3DDC97]">.</span>CRM</span>
          <nav className="hidden items-center gap-1 md:flex">
            <TabBtn id="pipeline" icon={LayoutGrid} label="Pipeline" />
            <TabBtn id="leads" icon={Table2} label="Leads" />
            <TabBtn id="chats" icon={MessagesSquare} label="Chats" />
            <TabBtn id="inhalte" icon={FileText} label="Inhalte" />
            <TabBtn id="settings" icon={KeyRound} label="Passwort" />
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="refresh-btn" onClick={refresh} className="rounded-md p-2 text-gray-400 hover:bg-white/5">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm hover:bg-white/5">
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      </header>

      <div className="flex gap-1 overflow-x-auto border-b border-white/10 px-4 py-2 md:hidden">
        <TabBtn id="pipeline" icon={LayoutGrid} label="Pipeline" />
        <TabBtn id="leads" icon={Table2} label="Leads" />
        <TabBtn id="chats" icon={MessagesSquare} label="Chats" />
        <TabBtn id="inhalte" icon={FileText} label="Inhalte" />
        <TabBtn id="settings" icon={KeyRound} label="Passwort" />
      </div>

      <main className="p-6">
        {tab === "pipeline" && <Pipeline leads={leads} setStatus={setStatus} onDelete={delLead} />}
        {tab === "leads" && (
          <LeadsTable
            leads={leads} selected={selected} toggleSel={toggleSel}
            onExport={exportCsv} onDelSelected={delSelected} onDelAll={delAllLeads}
            onDelete={delLead} setStatus={setStatus}
          />
        )}
        {tab === "chats" && (
          <Chats sessions={sessions} active={activeChat} setActive={setActiveChat}
            onDelete={delSession} onDelAll={delAllSessions} />
        )}
        {tab === "inhalte" && <ContentEditor hdr={hdr} />}
        {tab === "settings" && <PasswordSettings hdr={hdr} />}
      </main>
    </div>
  );
}

function ScoreBadge({ score }) {
  if (!score) return <span className="text-xs text-gray-600">–</span>;
  const color = score >= 8 ? "text-[#3DDC97]" : score >= 5 ? "text-amber-400" : "text-gray-400";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      <Star size={12} /> {score}/10
    </span>
  );
}

function Pipeline({ leads, setStatus, onDelete }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
      {STATUSES.map((s) => {
        const items = leads.filter((l) => l.status === s.key);
        return (
          <div key={s.key} className="w-72 shrink-0 rounded-lg bg-[#14161B] p-3" data-testid={`column-${s.key}`}>
            <div className="mb-3 flex items-center justify-between px-1">
              <span className="text-sm font-semibold text-white">{s.label}</span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-gray-400">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((l) => (
                <div key={l.id} className="group rounded-md border border-white/5 bg-[#1F222A] p-3" data-testid={`lead-card-${l.id}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-semibold text-white">{l.name || "Unbekannt"}</p>
                    <ScoreBadge score={l.score} />
                  </div>
                  {l.gewerk && <p className="mt-1 text-xs text-gray-400">{l.gewerk}</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
                    {l.phone && <span className="flex items-center gap-1"><Phone size={11} />{l.phone}</span>}
                    {l.region_plz && <span className="flex items-center gap-1"><MapPin size={11} />{l.region_plz}</span>}
                  </div>
                  {l.attribution?.gclid && (
                    <p className="mt-1 truncate text-[10px] text-[#3DDC97]/70">gclid: {l.attribution.gclid}</p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <select
                      data-testid={`status-select-${l.id}`}
                      value={l.status}
                      onChange={(e) => setStatus(l, e.target.value)}
                      className="flex-1 rounded border border-white/10 bg-[#0E0F12] px-2 py-1 text-xs text-gray-300"
                    >
                      {STATUSES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                    <button onClick={() => onDelete(l.id)} className="text-gray-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="px-1 py-4 text-xs text-gray-600">Keine Einträge</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadsTable({ leads, selected, toggleSel, onExport, onDelSelected, onDelAll, onDelete, setStatus }) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button data-testid="export-csv-btn" onClick={onExport} className="flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm hover:bg-white/5">
          <Download size={15} /> CSV-Export
        </button>
        <button data-testid="del-selected-btn" onClick={onDelSelected} disabled={!selected.length} className="flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm hover:bg-white/5 disabled:opacity-40">
          <Trash2 size={15} /> Auswahl löschen ({selected.length})
        </button>
        <button data-testid="del-all-btn" onClick={onDelAll} className="flex items-center gap-2 rounded-md border border-red-500/30 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10">
          <Trash2 size={15} /> Alle löschen
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-[#14161B] text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="p-3"></th>
              <th className="p-3">Name</th>
              <th className="p-3">Kontakt</th>
              <th className="p-3">Dienstleistung / Region</th>
              <th className="p-3">Score</th>
              <th className="p-3">Status</th>
              <th className="p-3">Quelle</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="p-3">
                  <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggleSel(l.id)} className="accent-[#3DDC97]" />
                </td>
                <td className="p-3 font-medium text-white">{l.name || "–"}</td>
                <td className="p-3 text-gray-400">
                  {l.phone && <div className="flex items-center gap-1"><Phone size={12} />{l.phone}</div>}
                  {l.email && <div className="flex items-center gap-1"><Mail size={12} />{l.email}</div>}
                </td>
                <td className="p-3 text-gray-400">{l.gewerk || "–"}{l.region_plz ? ` · ${l.region_plz}` : ""}</td>
                <td className="p-3"><ScoreBadge score={l.score} /></td>
                <td className="p-3">
                  <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} className="rounded border border-white/10 bg-[#0E0F12] px-2 py-1 text-xs">
                    {STATUSES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </td>
                <td className="p-3 text-xs text-gray-500">{l.source}</td>
                <td className="p-3">
                  <button onClick={() => onDelete(l.id)} className="text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-gray-600">Noch keine Leads.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Chats({ sessions, active, setActive, onDelete, onDelAll }) {
  return (
    <div className="grid gap-4 md:grid-cols-[320px_1fr]">
      <div className="rounded-lg border border-white/10 bg-[#14161B]">
        <div className="flex items-center justify-between border-b border-white/10 p-3">
          <span className="text-sm font-semibold text-white">Chatverläufe ({sessions.length})</span>
          <button data-testid="del-all-chats-btn" onClick={onDelAll} className="text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {sessions.map((s) => {
            const last = s.messages?.[s.messages.length - 1]?.content || "";
            return (
              <button
                key={s.session_id}
                data-testid={`chat-item-${s.session_id}`}
                onClick={() => setActive(s)}
                className={`block w-full border-b border-white/5 p-3 text-left hover:bg-white/[0.03] ${active?.session_id === s.session_id ? "bg-white/5" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-white">{s.lead_saved ? "✅ Lead" : "Chat"}</span>
                  <span className="text-[10px] text-gray-600">{(s.updated_at || "").slice(0, 10)}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-gray-400">{last}</p>
              </button>
            );
          })}
          {sessions.length === 0 && <p className="p-6 text-center text-xs text-gray-600">Keine Chats.</p>}
        </div>
      </div>
      <div className="rounded-lg border border-white/10 bg-[#14161B]">
        {active ? (
          <>
            <div className="flex items-center justify-between border-b border-white/10 p-3">
              <span className="text-sm font-semibold text-white">Verlauf</span>
              <button onClick={() => onDelete(active.session_id)} className="flex items-center gap-1 text-xs text-red-400 hover:underline">
                <Trash2 size={13} /> Löschen
              </button>
            </div>
            <div className="max-h-[56vh] space-y-3 overflow-y-auto p-4">
              {active.messages?.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-[#3DDC97] text-[#0E0F12]" : "bg-[#1F222A] text-gray-200"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="p-10 text-center text-sm text-gray-600">Chat auswählen.</p>
        )}
      </div>
    </div>
  );
}

function ContentEditor({ hdr }) {
  const [slug, setSlug] = useState("impressum");
  const [data, setData] = useState({ title: "", body: "" });
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (s) => {
    const { data } = await axios.get(`${API}/content/${s}`);
    setData({ title: data.title, body: data.body });
  }, []);
  useEffect(() => { load(slug); }, [slug, load]);

  const save = async () => {
    await axios.put(`${API}/content/${slug}`, { title: data.title, body: data.body }, hdr());
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-4 flex gap-2">
        {["impressum", "datenschutz"].map((s) => (
          <button key={s} onClick={() => setSlug(s)} className={`rounded-md px-3 py-2 text-sm capitalize ${slug === s ? "bg-[#3DDC97] text-[#0E0F12]" : "border border-white/10 text-gray-400"}`}>
            {s}
          </button>
        ))}
      </div>
      <input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} className="mb-3 w-full rounded-md border border-white/10 bg-[#0E0F12] px-3 py-2.5 text-sm text-white" placeholder="Titel" />
      <textarea data-testid="content-body" value={data.body} onChange={(e) => setData({ ...data, body: e.target.value })} rows={18} className="w-full rounded-md border border-white/10 bg-[#0E0F12] px-3 py-3 font-mono text-sm text-gray-200" />
      <button data-testid="content-save-btn" onClick={save} className="mt-3 flex items-center gap-2 rounded-md bg-[#3DDC97] px-5 py-2.5 text-sm font-semibold text-[#0E0F12] hover:bg-[#2BBF7E]">
        <Save size={15} /> {saved ? "Gespeichert!" : "Speichern"}
      </button>
    </div>
  );
}

function PasswordSettings({ hdr }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: next }, hdr());
      setMsg("Passwort geändert.");
      setCur(""); setNext("");
    } catch (err) {
      setMsg(err.response?.data?.detail || "Fehler.");
    }
  };
  const field = "w-full rounded-md border border-white/10 bg-[#0E0F12] px-3 py-2.5 text-sm text-white";
  return (
    <form onSubmit={submit} className="max-w-sm space-y-3" data-testid="password-form">
      <h2 className="font-display text-lg font-bold text-white">Passwort ändern</h2>
      <input className={field} type="password" placeholder="Aktuelles Passwort" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password" />
      <input className={field} type="password" placeholder="Neues Passwort (min. 8 Zeichen)" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password" />
      {msg && <p className="text-sm text-[#3DDC97]">{msg}</p>}
      <button type="submit" data-testid="change-password-btn" className="rounded-md bg-[#3DDC97] px-5 py-2.5 text-sm font-semibold text-[#0E0F12] hover:bg-[#2BBF7E]">Ändern</button>
    </form>
  );
}
